import type { ReactNode } from 'react';
import { FAIcon } from '~/components/utils/FAIcons';
import Button from '~/components/utils/buttons/Button';
import { openIntercomUpgrade } from './intercom';

type Variant = 'coming-soon' | 'enterprise';

interface Props {
	bullets: string[];
	children: ReactNode;
	className?: string;
	description: string;
	featureName: string;
	intercomMessage: string;
	variant?: Variant;
}

export const EnterprisePaywall = ({ bullets, children, className, description, featureName, intercomMessage, variant = 'enterprise' }: Props) => {
	const isComingSoon = variant === 'coming-soon';
	const badgeText = isComingSoon ? 'Coming Soon' : 'Enterprise';
	const ctaText = isComingSoon ? 'Get notified' : 'Upgrade';

	const accentGradient = isComingSoon ? 'from-amber-400 to-amber-600' : 'from-glazier-blue-500 to-glazier-blue-700';

	return (
		<div className={`relative isolate h-full w-full overflow-hidden ${className ?? ''}`}>
			<div aria-hidden="true" className="pointer-events-none h-full w-full scale-[1.02] select-none blur-[3px]">
				{children}
			</div>
			<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/10 px-4 backdrop-blur-[2px]">
				<div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5">
					<div className={`flex items-center justify-center bg-gradient-to-br ${accentGradient} px-6 py-5`}>
						<div className="flex size-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
							<FAIcon className="text-2xl text-white" icon={isComingSoon ? 'wand-magic-sparkles' : 'lock'} />
						</div>
					</div>
					<div className="p-6">
						<div className="flex items-center gap-2">
							<span
								className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
									isComingSoon ? 'bg-amber-100 text-amber-800' : 'bg-glazier-blue-100 text-glazier-blue-700'
								}`}
							>
								{badgeText}
							</span>
						</div>
						<h2 className="mt-3 text-xl font-bold text-gray-900">{featureName}</h2>
						<p className="mt-1 text-sm text-gray-600">{description}</p>
						{bullets.length > 0 && (
							<ul className="mt-4 space-y-2">
								{bullets.map((bullet) => (
									<li className="flex items-start gap-2 text-sm text-gray-700" key={bullet}>
										<FAIcon className="mt-0.5 text-glazier-blue-500" icon="check" />
										<span>{bullet}</span>
									</li>
								))}
							</ul>
						)}
						<div className="mt-5">
							<Button
								className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark"
								onClick={() => openIntercomUpgrade(intercomMessage)}
							>
								{ctaText}
								<FAIcon icon="arrow-right" />
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
